import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import ExcelJS from "exceljs";
import { API_BASE_URL, authFetch } from "../../config/api";

const emptyForm = { account_name: "", account_type: "", status: "active" };
const COMPANY_NAME = "RCS Logistics Philippines";
const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatLedgerDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" }) : "—";

const formatBalanceLabel = (value) => {
  const amount = Number(value || 0);
  if (amount === 0) return "₱0.00";
  return `₱${money(Math.abs(amount))} ${amount >= 0 ? "DR" : "CR"}`;
};

const ChartOfAccounts = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [ledgerAccount, setLedgerAccount] = useState(null);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState("");
  const [ledgerStartDate, setLedgerStartDate] = useState("");
  const [ledgerEndDate, setLedgerEndDate] = useState("");

  useEffect(() => {
    fetchList();
  }, []);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/chart-accounts`, {
        credentials: "include",
      });
      const json = await res.json();
      if (json.success) setList(json.data || []);
    } catch (err) {
      console.error("chart accounts", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const method = editingId ? "PUT" : "POST";
    const url = editingId
      ? `${API_BASE_URL}/api/accounting/chart-accounts/${editingId}`
      : `${API_BASE_URL}/api/accounting/chart-accounts`;
    try {
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (json.success) {
        setForm(emptyForm);
        setEditingId(null);
        setShowModal(false);
        fetchList();
      }
    } catch (err) {
      console.error("save coa", err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      account_name: row.account_name || "",
      account_type: row.account_type || "",
      status: row.status || "active",
    });
    setShowModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await authFetch(`${API_BASE_URL}/api/accounting/chart-accounts/${deleteTarget.id}`, {
      method: "DELETE",
      credentials: "include",
    });
    setDeleteTarget(null);
    fetchList();
  };

  const handleFilePick = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) return alert("No rows found in CSV.");
      setPreviewRows(rows);
      setShowImportModal(true);
    } catch (err) {
      console.error("read csv", err);
      alert("Could not read CSV file.");
    }
  };

  const confirmImport = async () => {
    if (!previewRows.length) return;
    setImporting(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/chart-accounts/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items: previewRows }),
      });
      const json = await res.json();
      if (!json.success) alert(json.message || "Import failed");
      else {
        setShowImportModal(false);
        setPreviewRows([]);
        fetchList();
      }
    } catch (err) {
      console.error("import", err);
      alert("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter(Boolean);
    const rows = [];
    for (const line of lines) {
      const [account_name = "", account_type = ""] = line.split(",");
      if (account_name.trim()) rows.push({ account_name: account_name.trim(), account_type: account_type.trim(), status: "active" });
    }
    return rows;
  };

  const openLedger = async (row) => {
    if (!row?.id) return;
    setLedgerAccount(row);
    setLedgerRows([]);
    setLedgerError("");
    setLedgerLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/chart-accounts/${row.id}/ledger`, {
        credentials: "include",
      });
      const json = await res.json();
      if (!json.success) {
        throw new Error(json.message || "Unable to load account ledger");
      }
      setLedgerAccount(json.data?.account || row);
      setLedgerRows(Array.isArray(json.data?.rows) ? json.data.rows : []);
    } catch (err) {
      console.error("load ledger", err);
      setLedgerError(err.message || "Unable to load account ledger");
    } finally {
      setLedgerLoading(false);
    }
  };

  const openOverallLedger = async () => {
    setLedgerAccount({
      id: "__overall__",
      account_name: "Overall Account Ledger",
      account_type: "All Accounts",
    });
    setLedgerRows([]);
    setLedgerError("");
    setLedgerLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/chart-accounts/ledger-overall`, {
        credentials: "include",
      });
      const json = await res.json();
      if (!json.success) {
        throw new Error(json.message || "Unable to load overall ledger");
      }
      setLedgerAccount(json.data?.account || {
        id: "__overall__",
        account_name: "Overall Account Ledger",
        account_type: "All Accounts",
      });
      setLedgerRows(Array.isArray(json.data?.rows) ? json.data.rows : []);
    } catch (err) {
      console.error("load overall ledger", err);
      setLedgerError(err.message || "Unable to load overall ledger");
    } finally {
      setLedgerLoading(false);
    }
  };

  const closeLedger = () => {
    setLedgerAccount(null);
    setLedgerRows([]);
    setLedgerError("");
    setLedgerLoading(false);
    setLedgerStartDate("");
    setLedgerEndDate("");
  };

  const exportLedger = async () => {
    if (!ledgerAccount || !ledgerRowsWithRunningBalance.length) return;

    const formatExcelDate = (value) => {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      const day = `${date.getDate()}`.padStart(2, "0");
      const month = `${date.getMonth() + 1}`.padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const generatedOn = ledgerEndDate || ledgerStartDate || new Date().toISOString().slice(0, 10);
    const generatedDateLabel = formatExcelDate(generatedOn);
    const titleSuffix = [ledgerAccount.account_no, ledgerAccount.account_code, ledgerAccount.code]
      .map((value) => String(value || "").trim())
      .find(Boolean);
    const title = titleSuffix
      ? `General Ledger - ${ledgerAccount.account_name || "Account"} (${titleSuffix})`
      : `General Ledger - ${ledgerAccount.account_name || "Account"}`;
    const firstCompany = COMPANY_NAME;
    const isOverallLedger = ledgerAccount?.id === "__overall__";
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("General Ledger", {
      views: [{ showGridLines: true }],
      properties: { defaultRowHeight: 21 },
    });

    worksheet.pageSetup = {
      paperSize: 9,
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: {
        left: 0.35,
        right: 0.35,
        top: 0.45,
        bottom: 0.45,
        header: 0.2,
        footer: 0.2,
      },
    };

    worksheet.columns = isOverallLedger
      ? [
          { key: "date", width: 16 },
          { key: "account", width: 28 },
          { key: "reference", width: 20 },
          { key: "checkNo", width: 14 },
          { key: "company", width: 24 },
          { key: "rfpJo", width: 24 },
          { key: "description", width: 30 },
          { key: "debit", width: 16 },
          { key: "credit", width: 16 },
          { key: "balance", width: 18 },
        ]
      : [
          { key: "date", width: 16 },
          { key: "reference", width: 20 },
          { key: "checkNo", width: 14 },
          { key: "company", width: 26 },
          { key: "rfpJo", width: 24 },
          { key: "description", width: 34 },
          { key: "debit", width: 16 },
          { key: "credit", width: 16 },
          { key: "balance", width: 18 },
        ];

    const allThinBorders = {
      top: { style: "thin", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FF000000" } },
      bottom: { style: "thin", color: { argb: "FF000000" } },
      right: { style: "thin", color: { argb: "FF000000" } },
    };
    const headerFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFDCEEFF" },
    };

    worksheet.mergeCells(`A1:${isOverallLedger ? "J" : "I"}1`);
    worksheet.getCell("A1").value = title;
    worksheet.getCell("A1").font = { name: "Calibri", size: 16, bold: true };
    worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
    worksheet.getRow(1).height = 28;

    worksheet.mergeCells(`A2:${isOverallLedger ? "J" : "I"}2`);
    worksheet.getCell("A2").value = `Company: ${firstCompany} | Generated on ${generatedDateLabel}`;
    worksheet.getCell("A2").font = { name: "Calibri", size: 11, bold: true };
    worksheet.getCell("A2").alignment = { horizontal: "center", vertical: "middle" };
    worksheet.getRow(2).height = 21;
    worksheet.getRow(3).height = 8;

    const headerRow = worksheet.getRow(4);
    headerRow.values = isOverallLedger
      ? ["Date", "Account", "Reference", "Check No.", "Payee / Company", "RFP / JO", "Description", "Debit", "Credit", "Balance"]
      : ["Date", "Reference", "Check No.", "Payee / Company", "RFP / JO", "Description", "Debit", "Credit", "Balance"];
    headerRow.height = 22;
    headerRow.eachCell((cell, colNumber) => {
      cell.font = { name: "Calibri", size: 11, bold: true };
      cell.alignment = { horizontal: colNumber >= 7 ? "center" : "center", vertical: "middle", wrapText: true };
      cell.fill = headerFill;
      cell.border = allThinBorders;
    });

    const dataStartRow = 5;
    ledgerRowsWithRunningBalance.forEach((row, index) => {
      const excelRow = worksheet.getRow(dataStartRow + index);
      excelRow.values = isOverallLedger
        ? [
            formatExcelDate(row.voucher_date),
            row.account_name || "",
            row.voucher_no || "",
            row.check_no || "",
            row.payee || firstCompany || "",
            Array.isArray(row.request_refs)
              ? row.request_refs
                  .map((ref) => [ref.rfp_no, ref.job_order].filter(Boolean).join(" / "))
                  .filter(Boolean)
                  .join(" | ")
              : "",
            row.particulars || ledgerAccount.account_name || "",
            Number(row.debit || 0),
            Number(row.credit || 0),
            formatBalanceLabel(row.filtered_running_balance).replace("₱", ""),
          ]
        : [
            formatExcelDate(row.voucher_date),
            row.voucher_no || "",
            row.check_no || "",
            row.payee || firstCompany || "",
            Array.isArray(row.request_refs)
              ? row.request_refs
                  .map((ref) => [ref.rfp_no, ref.job_order].filter(Boolean).join(" / "))
                  .filter(Boolean)
                  .join(" | ")
              : "",
            row.particulars || ledgerAccount.account_name || "",
            Number(row.debit || 0),
            Number(row.credit || 0),
            formatBalanceLabel(row.filtered_running_balance).replace("₱", ""),
          ];
      excelRow.height = 21;
      excelRow.eachCell((cell, colNumber) => {
        cell.font = { name: "Calibri", size: 11 };
        cell.alignment = {
          horizontal:
            colNumber >= (isOverallLedger ? 8 : 7) && colNumber <= (isOverallLedger ? 9 : 8)
              ? "right"
              : colNumber === (isOverallLedger ? 10 : 9)
                ? "right"
                : "left",
          vertical: "middle",
          wrapText: isOverallLedger
            ? colNumber === 2 || colNumber === 5 || colNumber === 6 || colNumber === 7
            : colNumber === 4 || colNumber === 5 || colNumber === 6,
        };
        cell.border = allThinBorders;
        if (
          colNumber === (isOverallLedger ? 8 : 7) ||
          colNumber === (isOverallLedger ? 9 : 8)
        ) {
          cell.numFmt = '#,##0.00;[Red]-#,##0.00';
        }
      });
    });

    const totalRowNumber = dataStartRow + ledgerRowsWithRunningBalance.length;
    worksheet.mergeCells(`A${totalRowNumber}:${isOverallLedger ? "G" : "F"}${totalRowNumber}`);
    const totalRow = worksheet.getRow(totalRowNumber);
    totalRow.getCell(1).value = "TOTAL";
    totalRow.getCell(isOverallLedger ? 8 : 7).value = {
      formula: `SUM(${isOverallLedger ? "H" : "G"}${dataStartRow}:${isOverallLedger ? "H" : "G"}${totalRowNumber - 1})`,
      result: Number(filteredLedgerTotals.debit || 0),
    };
    totalRow.getCell(isOverallLedger ? 9 : 8).value = {
      formula: `SUM(${isOverallLedger ? "I" : "H"}${dataStartRow}:${isOverallLedger ? "I" : "H"}${totalRowNumber - 1})`,
      result: Number(filteredLedgerTotals.credit || 0),
    };
    totalRow.getCell(isOverallLedger ? 10 : 9).value = formatBalanceLabel(filteredLedgerTotals.balance).replace("₱", "");
    totalRow.height = 22;

    (isOverallLedger
      ? ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"]
      : ["A", "B", "C", "D", "E", "F", "G", "H", "I"]).forEach((column) => {
      const cell = worksheet.getCell(`${column}${totalRowNumber}`);
      cell.font = { name: "Calibri", size: 11, bold: true };
      cell.alignment = {
        horizontal:
          column === "A"
            ? "center"
            : ["G", "H", "I", "J"].includes(column) && isOverallLedger
              ? ["H", "I", "J"].includes(column)
                ? "right"
                : "left"
              : ["G", "H", "I"].includes(column) && !isOverallLedger
                ? ["G", "H", "I"].includes(column)
                  ? (column === "I" || column === "H" || column === "G" ? "right" : "left")
                  : "left"
                : "left",
        vertical: "middle",
        wrapText: isOverallLedger
          ? ["B", "E", "F", "G"].includes(column)
          : ["D", "E", "F"].includes(column),
      };
      cell.fill = headerFill;
      cell.border = allThinBorders;
      if (
        column === (isOverallLedger ? "H" : "G") ||
        column === (isOverallLedger ? "I" : "H")
      ) {
        cell.numFmt = '#,##0.00;[Red]-#,##0.00';
      }
    });

    const accountName = String(ledgerAccount.account_name || "account")
      .trim()
      .replace(/[<>:"/\\|?*]+/g, "_")
      .replace(/\s+/g, "-");
    const dateSuffix =
      ledgerStartDate || ledgerEndDate
        ? `-${ledgerStartDate || "start"}-to-${ledgerEndDate || "end"}`
        : "";
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `general-ledger-${accountName}${dateSuffix}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filteredLedgerRows = useMemo(() => {
    return ledgerRows.filter((row) => {
      const rowDate = String(row.voucher_date || "").slice(0, 10);
      if (!rowDate) return !ledgerStartDate && !ledgerEndDate;
      if (ledgerStartDate && rowDate < ledgerStartDate) return false;
      if (ledgerEndDate && rowDate > ledgerEndDate) return false;
      return true;
    });
  }, [ledgerEndDate, ledgerRows, ledgerStartDate]);

  const filteredLedgerTotals = useMemo(() => {
    const totals = filteredLedgerRows.reduce(
      (acc, row) => ({
        debit: acc.debit + Number(row.debit || 0),
        credit: acc.credit + Number(row.credit || 0),
      }),
      { debit: 0, credit: 0 }
    );

    return {
      debit: Number(totals.debit.toFixed(2)),
      credit: Number(totals.credit.toFixed(2)),
      balance: Number((totals.debit - totals.credit).toFixed(2)),
    };
  }, [filteredLedgerRows]);

  const ledgerRowsWithRunningBalance = useMemo(() => {
    let runningBalance = 0;
    return filteredLedgerRows.map((row) => {
      runningBalance += Number(row.debit || 0) - Number(row.credit || 0);
      return {
        ...row,
        filtered_running_balance: Number(runningBalance.toFixed(2)),
      };
    });
  }, [filteredLedgerRows]);

  const ledgerPeriodLabel = useMemo(() => {
    if (!ledgerStartDate && !ledgerEndDate) return "All Dates";
    return `${ledgerStartDate || "Beginning"} to ${ledgerEndDate || "Present"}`;
  }, [ledgerEndDate, ledgerStartDate]);

  const isOverallLedger = ledgerAccount?.id === "__overall__";

  return (
    <div className="space-y-6 font-poppins">
      <div className="bg-white/95 backdrop-blur-sm border border-gray-100 rounded-3xl shadow-sm p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-gray-500">Accounting</p>
          <h1 className="text-2xl font-semibold text-blue">Chart of Accounts</h1>
          <p className="text-sm text-gray-600">Maintain account names and types.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={openOverallLedger}
            className="px-4 py-2 rounded-lg border border-blue/20 bg-blue-50 text-blue text-sm font-semibold hover:bg-blue-100"
          >
            Overall Ledger
          </button>
          <button
            onClick={() => {
              setEditingId(null);
              setForm(emptyForm);
              setShowModal(true);
            }}
            className="px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue/90 shadow-sm"
          >
            + Add Account
          </button>
          <label className="relative">
            <input
              type="file"
              accept=".csv,text/csv"
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={(e) => handleFilePick(e.target.files?.[0])}
              disabled={importing}
            />
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer">
              <i className="fa-solid fa-file-import"></i> Import CSV
            </span>
          </label>
          <button
            onClick={fetchList}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-gray-300"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
        <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#f8fbff_0%,#eef4fb_100%)] px-5 py-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Account Directory</p>
              <h3 className="mt-1 text-lg font-semibold text-slate-900">Chart of Accounts Register</h3>
              <p className="mt-1 text-sm text-slate-600">Classifications, activity count, and maintenance actions.</p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm lg:w-auto">
              <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Total Accounts</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{list.length}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Active</div>
                <div className="mt-1 text-lg font-semibold text-emerald-700">
                  {list.filter((row) => row.status === "active").length}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Transactions</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">
                  {list.reduce((sum, row) => sum + Number(row.transaction_count || 0), 0).toLocaleString("en-US")}
                </div>
              </div>
            </div>
          </div>
          {loading && <div className="mt-4 text-sm font-medium text-slate-500">Refreshing account register…</div>}
        </div>

        <div className="bg-white">
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/90 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                <th className="w-[34%] px-5 py-3">Account</th>
                <th className="w-[14%] px-3 py-3">Classification</th>
                <th className="w-[12%] px-3 py-3 text-center">Transactions</th>
                <th className="w-[12%] px-3 py-3 text-center">Status</th>
                <th className="w-[12%] px-3 py-3">Created</th>
                <th className="w-[16%] px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((row) => {
                const transactionCount = Number(row.transaction_count || 0);
                const createdLabel = row.created_at ? new Date(row.created_at).toLocaleDateString() : "—";

                return (
                  <tr key={row.id} className="group transition-colors hover:bg-slate-50/70">
                    <td className="px-5 py-3.5">
                      <div className="flex items-start gap-2.5">
                        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                          <i className="fa-solid fa-book text-xs"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="break-words text-[14px] font-semibold leading-5 text-slate-900">{row.account_name}</div>
                          <div className="mt-0.5 text-[11px] text-slate-500">
                            {transactionCount ? "Recorded ledger activity" : "No ledger activity yet"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3.5 align-top">
                      <span className="inline-flex max-w-full rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 shadow-sm break-words">
                        {row.account_type || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-3.5 text-center align-top">
                      <div className="inline-flex min-w-[4.5rem] items-center justify-center rounded-xl bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm">
                        {transactionCount.toLocaleString("en-US")}
                      </div>
                    </td>
                    <td className="px-3 py-3.5 text-center align-top">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                          row.status === "active"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-3.5 align-top">
                      <div className="text-sm font-medium text-slate-800">{createdLabel}</div>
                      <div className="mt-0.5 text-[11px] text-slate-500">Created</div>
                    </td>
                    <td className="px-5 py-3.5 align-top">
                      <div className="flex flex-col items-end gap-1.5">
                        <div className="flex items-center gap-2">
                          <button
                            className="rounded-full bg-blue px-2.5 py-1 text-[11px] font-semibold text-white transition hover:bg-blue/90"
                            onClick={() => handleEdit(row)}
                          >
                            Edit
                          </button>
                          <button
                            className="rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red transition hover:bg-red-100"
                            onClick={() => setDeleteTarget(row)}
                          >
                            Delete
                          </button>
                        </div>
                        <button
                          className="rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-200"
                          onClick={() => openLedger(row)}
                        >
                          View Ledger
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!list.length && (
                <tr>
                  <td className="px-6 py-12 text-center text-slate-500" colSpan={6}>
                    No accounts yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <h3 className="text-lg font-semibold text-blue mb-4">
                {editingId ? "Edit Account" : "Add Account"}
              </h3>
              <form className="space-y-4" onSubmit={handleSave}>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Name</label>
                  <input
                    required
                    value={form.account_name}
                    onChange={(e) => setForm({ ...form, account_name: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Type</label>
                  <input
                    required
                    value={form.account_type}
                    onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue/90 disabled:opacity-60"
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {deleteTarget &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 relative">
              <button
                onClick={() => setDeleteTarget(null)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <h3 className="text-lg font-semibold text-red mb-2">Delete Account</h3>
              <p className="text-sm text-gray-700 mb-4">Are you sure you want to delete this account?</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 rounded-lg bg-red text-white text-sm font-semibold hover:bg-red/90"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showImportModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6 relative">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setPreviewRows([]);
                }}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <h3 className="text-lg font-semibold text-blue mb-2">Import Preview</h3>
              <p className="text-sm text-gray-700 mb-3">CSV columns: account_name, account_type</p>
              <div className="border border-gray-100 rounded-xl overflow-hidden max-h-72 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                      <th className="px-4 py-2">Account Name</th>
                      <th className="px-4 py-2">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewRows.map((r, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2">{r.account_name}</td>
                        <td className="px-4 py-2">{r.account_type}</td>
                      </tr>
                    ))}
                    {!previewRows.length && (
                      <tr>
                        <td className="px-4 py-3 text-center text-gray-500" colSpan={2}>
                          No rows
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setPreviewRows([]);
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmImport}
                  disabled={importing}
                  className="px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue/90 disabled:opacity-60"
                >
                  {importing ? "Importing..." : "Import"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {ledgerAccount &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 backdrop-blur-sm px-4 py-6">
            <div className="mx-auto flex min-h-full items-center justify-center">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl p-6 relative max-h-[90vh] overflow-hidden flex flex-col">
              <button
                onClick={closeLedger}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <div className="pr-10">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">General Ledger</p>
                <h3 className="mt-1 text-2xl font-semibold text-blue">{ledgerAccount.account_name || "Account"}</h3>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-700">
                  <div><span className="font-semibold text-slate-500">Account Type:</span> {ledgerAccount.account_type || "—"}</div>
                  <div><span className="font-semibold text-slate-500">Period:</span> {ledgerPeriodLabel}</div>
                  <div><span className="font-semibold text-slate-500">Transactions:</span> {ledgerRowsWithRunningBalance.length}</div>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Total Debit</div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">₱{money(filteredLedgerTotals.debit)}</div>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Total Credit</div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">₱{money(filteredLedgerTotals.credit)}</div>
                </div>
                <div className="rounded-2xl bg-blue-50 p-4">
                  <div className="text-xs uppercase tracking-[0.14em] text-blue-700">Ending Balance</div>
                  <div className="mt-2 text-2xl font-semibold text-blue">{formatBalanceLabel(filteredLedgerTotals.balance)}</div>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                      Date From
                    </label>
                    <input
                      type="date"
                      value={ledgerStartDate}
                      onChange={(e) => setLedgerStartDate(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                      Date To
                    </label>
                    <input
                      type="date"
                      value={ledgerEndDate}
                      onChange={(e) => setLedgerEndDate(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm text-slate-500">
                    {ledgerRowsWithRunningBalance.length} transaction(s)
                  </div>
                  <button
                    onClick={exportLedger}
                    disabled={!ledgerRowsWithRunningBalance.length}
                    className="px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue/90 disabled:opacity-60"
                  >
                    Export Excel
                  </button>
                  <button
                    onClick={() => {
                      setLedgerStartDate("");
                      setLedgerEndDate("");
                    }}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Clear Dates
                  </button>
                </div>
              </div>

              {ledgerError && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {ledgerError}
                </div>
              )}

              <div className="mt-4 min-h-0 flex-1 overflow-hidden rounded-2xl border border-gray-100">
                <div
                  className="min-h-0 overflow-auto overscroll-contain pb-4"
                  style={{ maxHeight: "min(45vh, calc(90vh - 22rem))" }}
                >
                  <table className={`w-full text-sm ${isOverallLedger ? "min-w-[1360px]" : "min-w-[1200px]"}`}>
                    <thead className="bg-gray-50 text-gray-700 sticky top-0">
                      <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                        <th className="px-4 py-3">Date</th>
                        {isOverallLedger ? <th className="px-4 py-3">Account</th> : null}
                        <th className="px-4 py-3">Reference</th>
                        <th className="px-4 py-3">Check No.</th>
                        <th className="px-4 py-3">Payee</th>
                        <th className="px-4 py-3">RFP / JO</th>
                        <th className="px-4 py-3">Description</th>
                        <th className="px-4 py-3 text-right">Debit</th>
                        <th className="px-4 py-3 text-right">Credit</th>
                        <th className="px-4 py-3 text-right">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ledgerLoading ? (
                        <tr>
                          <td className="px-4 py-8 text-center text-gray-500" colSpan={isOverallLedger ? 10 : 9}>
                            Loading ledger...
                          </td>
                        </tr>
                      ) : ledgerRowsWithRunningBalance.length ? (
                        ledgerRowsWithRunningBalance.map((row) => (
                          <tr key={row.id} className="align-top hover:bg-gray-50/70">
                            <td className="px-4 py-3 text-gray-700">
                              {formatLedgerDate(row.voucher_date)}
                            </td>
                            {isOverallLedger ? (
                              <td className="px-4 py-3 text-gray-700 font-medium">{row.account_name || "—"}</td>
                            ) : null}
                            <td className="px-4 py-3">
                              <div className="font-medium text-blue">{row.voucher_no || "—"}</div>
                              <div className="text-xs text-slate-500">{row.voucher_status || "posted"}</div>
                            </td>
                            <td className="px-4 py-3 text-gray-700">{row.check_no || "—"}</td>
                            <td className="px-4 py-3 text-gray-700">{row.payee || "—"}</td>
                            <td className="px-4 py-3 text-gray-700">
                              {row.request_refs?.length ? (
                                <div className="space-y-1">
                                  {row.request_refs.map((ref) => (
                                    <div key={ref.id || `${ref.rfp_no}-${ref.job_order}`}>
                                      {ref.rfp_no || "—"}
                                      {ref.job_order ? ` · ${ref.job_order}` : ""}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-700">{row.particulars || "—"}</td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              {row.debit ? `₱${money(row.debit)}` : "—"}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              {row.credit ? `₱${money(row.credit)}` : "—"}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-900">
                              {formatBalanceLabel(row.filtered_running_balance)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="px-4 py-8 text-center text-gray-500" colSpan={isOverallLedger ? 10 : 9}>
                            No transactions found for this account.
                          </td>
                        </tr>
                      )}
                      {!ledgerLoading && ledgerRowsWithRunningBalance.length ? (
                        <tr className="bg-slate-50 font-semibold">
                          <td className="px-4 py-3 text-slate-700" colSpan={isOverallLedger ? 7 : 6}>
                            Period Totals
                          </td>
                          <td className="px-4 py-3 text-right text-slate-900">₱{money(filteredLedgerTotals.debit)}</td>
                          <td className="px-4 py-3 text-right text-slate-900">₱{money(filteredLedgerTotals.credit)}</td>
                          <td className="px-4 py-3 text-right text-slate-900">{formatBalanceLabel(filteredLedgerTotals.balance)}</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={closeLedger}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ChartOfAccounts;
